import { IndexeddbPersistence } from 'y-indexeddb';
import type * as Y from 'yjs';
import { detectOS, formatCombo, matchesCombo, parseCombo } from '../shortcuts';
import type { Combo, Platform, PickedFile, ShortcutEventLike } from '../types';

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT';
}

export function createWebPlatform(win: Window = window): Platform {
  const doc = win.document;
  const os = detectOS(win.navigator.userAgent, win.navigator.platform);
  const isTouch = win.matchMedia('(pointer: coarse)').matches;
  const persisted = new Map<string, IndexeddbPersistence>();

  const storage = {
    get(key: string) {
      try { return win.localStorage.getItem(key); } catch { return null; }
    },
    set(key: string, value: string) {
      try { win.localStorage.setItem(key, value); } catch { /* storage unavailable */ }
    },
    remove(key: string) {
      try { win.localStorage.removeItem(key); } catch { /* storage unavailable */ }
    },
  };

  // Clipboard falls back to an in-memory buffer when the async clipboard API is denied
  // (e.g. insecure contexts or missing permission), so copy/paste inside the app still works.
  let memoryClipboard = { text: '', html: '' };
  const clipboard = {
    async writeText(text: string) {
      memoryClipboard = { text, html: '' };
      try { await win.navigator.clipboard.writeText(text); } catch { /* fallback buffer kept */ }
    },
    async write(data: { text: string; html?: string }) {
      memoryClipboard = { text: data.text, html: data.html ?? '' };
      try {
        if (data.html && 'ClipboardItem' in win) {
          await win.navigator.clipboard.write([
            new ClipboardItem({ 'text/plain': new Blob([data.text], { type: 'text/plain' }), 'text/html': new Blob([data.html], { type: 'text/html' }) }),
          ]);
        } else await win.navigator.clipboard.writeText(data.text);
      } catch { /* fallback buffer kept */ }
    },
    async readText() {
      try { return await win.navigator.clipboard.readText(); } catch { return memoryClipboard.text; }
    },
  };

  const files = {
    pick(options: { accept?: string; multiple?: boolean } = {}) {
      return new Promise<PickedFile[]>((resolve) => {
        const input = doc.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        if (options.accept) input.accept = options.accept;
        input.multiple = !!options.multiple;
        input.addEventListener('change', () => {
          const list = Array.from(input.files ?? []).map((f) => ({ name: f.name, type: f.type, size: f.size, blob: f }));
          input.remove();
          resolve(list);
        });
        input.addEventListener('cancel', () => { input.remove(); resolve([]); });
        doc.body.appendChild(input);
        input.click();
      });
    },
    async save(filename: string, blob: Blob) {
      const url = URL.createObjectURL(blob);
      const a = doc.createElement('a');
      a.href = url;
      a.download = filename;
      doc.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
  };

  type Entry = { combos: Combo[]; handler: (e: ShortcutEventLike) => boolean | void; allowInInputs: boolean };
  const entries = new Set<Entry>();
  doc.addEventListener('keydown', (e) => {
    for (const entry of [...entries].reverse()) {
      if (!entry.allowInInputs && isEditableTarget(e.target)) continue;
      if (entry.combos.some((c) => matchesCombo(e, c, os))) {
        const r = entry.handler(e);
        if (r !== false) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }
  }, true);

  const shortcuts = {
    register(combo: Combo | Combo[], handler: Entry['handler'], options: { allowInInputs?: boolean } = {}) {
      const entry: Entry = { combos: Array.isArray(combo) ? combo : [combo], handler, allowInInputs: options.allowInInputs ?? true };
      entries.add(entry);
      return () => { entries.delete(entry); };
    },
    matches: (e: ShortcutEventLike, combo: Combo) => matchesCombo(e, combo, os),
    format: (combo: Combo) => formatCombo(combo, os),
    trigger(combo: Combo) {
      const p = parseCombo(combo);
      const apple = os === 'mac' || os === 'ios';
      const key = p.key;
      const fake: ShortcutEventLike = { key, code: '', metaKey: p.meta || (p.mod && apple), ctrlKey: p.ctrl || (p.mod && !apple), altKey: p.alt, shiftKey: p.shift };
      for (const entry of [...entries].reverse()) {
        if (entry.combos.some((c) => matchesCombo(fake, c, os)) && entry.handler(fake) !== false) return true;
      }
      return false;
    },
  };

  const network = {
    get online() { return win.navigator.onLine; },
    subscribe(listener: (online: boolean) => void) {
      const on = () => listener(true);
      const off = () => listener(false);
      win.addEventListener('online', on);
      win.addEventListener('offline', off);
      return () => { win.removeEventListener('online', on); win.removeEventListener('offline', off); };
    },
  };

  const persistence = {
    attach(name: string, ydoc: Y.Doc) {
      const p = new IndexeddbPersistence(`lumen:${name}`, ydoc);
      persisted.set(name, p);
      return {
        whenLoaded: p.whenSynced.then(() => undefined),
        destroy: () => { persisted.delete(name); void p.destroy(); },
      };
    },
    async clearAll() {
      // Open documents stop storing first: an edit after the delete (a closing page writes its text once more)
      // would otherwise hit a closed database and throw.
      const open = [...persisted.values()];
      persisted.clear();
      await Promise.all(open.map((p) => p.destroy()));
      const dbs = (await win.indexedDB.databases?.()) ?? [];
      await Promise.all(
        dbs.filter((d) => d.name?.startsWith('lumen:')).map((d) => new Promise<void>((res) => {
          const r = win.indexedDB.deleteDatabase(d.name!);
          r.onsuccess = r.onerror = r.onblocked = () => res();
        })),
      );
    },
  };

  const mq = win.matchMedia('(prefers-color-scheme: dark)');
  const colorScheme = {
    get: () => (mq.matches ? 'dark' : 'light') as 'light' | 'dark',
    subscribe(listener: (s: 'light' | 'dark') => void) {
      const fn = () => listener(mq.matches ? 'dark' : 'light');
      mq.addEventListener('change', fn);
      return () => mq.removeEventListener('change', fn);
    },
  };

  const navListeners = new Set<() => void>();
  win.addEventListener('popstate', () => navListeners.forEach((l) => l()));
  const navigation = {
    current: () => ({ path: win.location.pathname, hash: win.location.hash.slice(1), query: win.location.search.slice(1) }),
    push(path: string) {
      win.history.pushState(null, '', path);
      navListeners.forEach((l) => l());
    },
    replace(path: string) {
      win.history.replaceState(null, '', path);
      navListeners.forEach((l) => l());
    },
    back: () => win.history.back(),
    forward: () => win.history.forward(),
    subscribe(listener: () => void) {
      navListeners.add(listener);
      return () => navListeners.delete(listener);
    },
    absoluteUrl: (path: string) => new URL(path, win.location.origin).toString(),
  };

  return {
    navigation,
    info: { os, isTouch },
    storage,
    clipboard,
    files,
    shortcuts,
    network,
    persistence,
    colorScheme,
    openExternal: (url) => { win.open(url, '_blank', 'noopener,noreferrer'); },
    openInNewTab: (path) => { win.open(path, '_blank'); },
    installOfflineShell(scriptUrl: string) {
      if (!('serviceWorker' in win.navigator)) return;
      // After the page has loaded, so caching the shell does not compete with the first load.
      const register = () => void win.navigator.serviceWorker.register(scriptUrl).catch(() => { /* offline shell is best effort */ });
      if (win.document.readyState === 'complete') register();
      else win.addEventListener('load', register, { once: true });
    },
    print: () => win.print(),
  };
}
